import { useSelector, useDispatch } from "react-redux";
import GridTop from "../new_taxonomy/grid-top";
import ContentModulesContainer from "../shared/content-modules-container";
import { childrenPropType } from "../../propTypes";
import { GridFilterModal } from "./grid-filter-modal";
import { replaceGridUrlPath, changeFilters } from "../../../actions/products-actions";
import { fetchProducts } from "../../../slices/products-slice";
import BottomGridContent from "./bottom-grid-content";
import { useEffect, useState, useRef } from "react";
import { RENTAL_INTENT, QUERY_PARAMS, NEXT_GRID_TYPES } from "rtr-constants";
import GridStickyCta from "../new_taxonomy/grid-sticky-cta";
import VisualNav from "../new_taxonomy/visual-nav";
import { getDateOptions } from "helpers/date-helpers";
import { getRentalIntent } from "helpers/rental-intent-helper";
import dynamic from "next/dynamic";
import { useQueryParams } from "../hoc/with-query-params";
import HeapHelpers from "helpers/heap-helpers";

const GridProductDrawer = dynamic(() => import("./grid-product-drawer/grid-product-drawer"), {
  ssr: false,
});

export const GridContainer = props => {
  const visualNavCtaRef = useRef(null);
  const { children } = props;
  const [displayStickyCta, setDisplayStickyCta] = useState(false);
  const gridTopperContent = useSelector(state => state.gridTopperContent);
  const hideGridHeader = useSelector(state => state.hideGridHeader);
  const gridCount = useSelector(state => state.gridCount);
  const filterOptions = useSelector(state => state.filterOptions);
  const workingFilters = useSelector(state => state.workingFilters);
  const currentPage = useSelector(state => state.currentPage);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const isSearchResultGrid = useSelector(state => !!state.workingFilters?.searchText);
  const dateOptions = useSelector(state => state.dateOptions);
  const productDrawerOpen = useSelector(state => state.gridProductDrawer);
  const currentIntent = useSelector(state => getRentalIntent(state?.userData));
  const sailthruConfig = useSelector(state => ({
    customerId: state.publicEnv.sailthruCustomerId,
    url: state.initialUrl.split("?")[0],
  }));

  // for heap tracking ABC testing on clearance grid sort
  const workingSort = useSelector(state => state.workingSort);
  const userId = useSelector(state => state.userId);
  const gridType = useSelector(state => state.gridType);
  const hearts = useSelector(state => state.favorites);
  const [isHeapEventSent, setIsHeapEventSent] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    // anonymous user, will not have hearts
    if (!userId) {
      sendDefaultSortHeapEvent(false);
    }

    // This config is picked up once the `oneTrustLoadEvent` event fires
    // see sailthru-helpers.js
    window.sailthruConfig = sailthruConfig;
    window.addEventListener("scroll", handleScroll);
    return function () {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // wait until hearts are loaded before firing event
    if (hearts) {
      sendDefaultSortHeapEvent(hearts.length > 0);
    }
  }, [hearts]);

  const onRemoveFilter = (filterKey, filterValue) => {
    const updatedFilters = { ...workingFilters };

    if (Array.isArray(updatedFilters[filterKey])) {
      // If it's an array, filter out the specified value
      updatedFilters[filterKey] = updatedFilters[filterKey].filter(val => val !== filterValue);
    } else if (typeof updatedFilters[filterKey] === "string") {
      // If it's a string, only remove it if it matches filterValue
      updatedFilters[filterKey] = ""; // Remove the key if the value matches
    }

    dispatch(changeFilters(updatedFilters));
    handleFilterChange(); // Update products accordingly
  };

  const submitFilters = () => {
    handleFilterChange();
  };

  // We always send the user back to page 1 on a filter page
  const handleFilterChange = () => {
    const page = 1;
    dispatch(replaceGridUrlPath(page));
    dispatch(fetchProducts(page));
  };

  // On a sort change we maintain the current page
  const handleSortChange = () => {
    dispatch(replaceGridUrlPath(currentPage));
    dispatch(fetchProducts(currentPage));
  };

  const getVisualNavTopPosition = () => {
    return visualNavCtaRef.current.getBoundingClientRect().y;
  };

  // porting over old code
  const handleScroll = () => {
    if (shouldShowStickyCta()) {
      setDisplayStickyCta(true);
    }
  };

  // porting over old code
  const shouldShowStickyCta = () => {
    const bottomNavDisplayed = window.scrollY < document.body.scrollHeight - 1600;
    return (
      isMobileViewport &&
      getVisualNavTopPosition() <= 0 &&
      bottomNavDisplayed &&
      currentIntent !== RENTAL_INTENT.RESERVE &&
      !workingFilters?.date
    );
  };

  const renderProductDrawer = () => {
    return productDrawerOpen ? <GridProductDrawer /> : null;
  };

  const showVisualNav = () => {
    //these actions are only appended to the url if the user navigates to the grid from the shortlists page or the shortlists dropdown. Dependent on the structure of lib/views/shortlist/index.erb.
    const action = useQueryParams(QUERY_PARAMS.ACTION);

    if (
      action.queryParams.action === "click_heartSubmenu" ||
      action.queryParams.action === "click_overlay" ||
      isSearchResultGrid
    ) {
      return null;
    } else {
      return <VisualNav />;
    }
  };

  const sendDefaultSortHeapEvent = hasHearts => {
    if (!isHeapEventSent && gridType === NEXT_GRID_TYPES.clearance) {
      HeapHelpers.fireHeapEvent("grid_loaded", {
        default_sort: workingSort,
        grid_type: NEXT_GRID_TYPES.clearance,
        has_hearts: hasHearts,
      });
      setIsHeapEventSent(true);
    }
  };

  return (
    <div className="relative" ref={visualNavCtaRef}>
      <ContentModulesContainer
        contentModules={gridTopperContent?.contentModules}
        fromCache={gridTopperContent?.fromCache}
      />
      {showVisualNav()}
      <GridTop
        title="View all"
        resultsCount={gridCount}
        filterOptions={filterOptions}
        submitFilters={submitFilters}
        showQuickFilters
        isDeadEndTextSearch={false}
        onRemoveFilter={onRemoveFilter}
        hideGridHeader={hideGridHeader}
        currentIntent={currentIntent}
        dateOptions={getDateOptions(dateOptions)}
      />
      {children}
      <GridFilterModal handleFilterChange={handleFilterChange} handleSortChange={handleSortChange} />
      <BottomGridContent />
      {/* porting over old code */}
      <GridStickyCta isVisible={displayStickyCta} />
      {renderProductDrawer()}
    </div>
  );
};

GridContainer.propTypes = {
  children: childrenPropType,
};
